import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectDirectory,
  setDirectories,
} from "../../../state/reducers/resourcesSlice";
import DocumentUpload from "./DocumentUpload";
import axios from "axios";
import {
  FiFolder,
  FiFile,
  FiDownload,
  FiTrash,
  FiEdit,
  FiUpload,
  FiRefreshCw,
  FiPlus,
  FiArrowLeft,
  FiSearch,
  FiCheck,
  FiX,
} from "react-icons/fi";

const Resources = () => {
  const [activeTab, setActiveTab] = useState("students");
  const [activeTab2, setActiveTab2] = useState("fileManager");
  const [newDirectoryName, setNewDirectoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "upload", "newFolder", etc.
  const [sortColumn, setSortColumn] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // New states for folder editing
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderValue, setEditFolderValue] = useState("");

  const getTabType = () => {
    return activeTab === "students" ? "Students" : "Internal Team";
  };

  const getFileExtension = (fileName) => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "unknown";
  };

  const dispatch = useDispatch();
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const directories = useSelector((state) => state.resources.directories);
  const selectedDirectory = useSelector(
    (state) => state.resources.selectedDirectory
  );

  const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8000";

  // Fetch directories
  const fetchDirectories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/files/`, {
        params: {
          tab_type: getTabType(),
          parent_directory: selectedDirectory || "",
        },
      });
      if (response.status === 200) {
        dispatch(setDirectories(response.data));
      }
    } catch (error) {
      console.error(
        "Failed to fetch directories:",
        error.response?.data || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectories();
  }, [selectedDirectory, activeTab]);

  const handleAddDirectory = async () => {
    if (newDirectoryName) {
      setLoading(true);
      const formData = new FormData();
      formData.append("folder_name", newDirectoryName);
      formData.append("parent_directory", selectedDirectory || "");
      formData.append("tab_type", getTabType());

      try {
        const response = await axios.post(`${BASE_URL}/api/files/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.status === 201) {
          setNewDirectoryName("");
          fetchDirectories();
          setShowModal(false);
        }
      } catch (error) {
        console.error(
          "Failed to create directory:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUploadFile = async () => {
    if (fileToUpload) {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("parent_directory", selectedDirectory);
      formData.append("tab_type", getTabType());

      try {
        const response = await axios.post(`${BASE_URL}/api/files/`, formData);
        setFileToUpload(null);
        fetchDirectories();
        setShowModal(false);
      } catch (error) {
        console.error(
          "Failed to upload file:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Start editing a file name
  const startEditingFile = (file) => {
    setEditMode(file.name);
    setEditValue(file.name);
  };

  // Cancel editing file
  const cancelEditing = () => {
    setEditMode(null);
    setEditValue("");
  };

  // Handle edit file name
  const handleEditFile = async (fileId, newFileName) => {
    if (newFileName && newFileName.trim() !== "") {
      setLoading(true);
      try {
        const response = await axios.patch(`${BASE_URL}/api/files/${fileId}/`, {
          name: newFileName,
        });

        if (response.status === 200) {
          fetchDirectories();
          setEditMode(null);
          setEditValue("");
        }
      } catch (error) {
        console.error(
          "Failed to edit file:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Start editing folder name
  const startEditingFolder = (folderName) => {
    setEditingFolder(folderName);
    setEditFolderValue(folderName);
  };

  // Cancel editing folder
  const cancelEditingFolder = () => {
    setEditingFolder(null);
    setEditFolderValue("");
  };

  // Handle folder rename
  const handleEditFolder = async (oldFolderName, newFolderName) => {
    if (
      newFolderName &&
      newFolderName.trim() !== "" &&
      newFolderName !== oldFolderName
    ) {
      setLoading(true);
      try {
        // Construct the old path based on selected directory and folder name
        const oldPath = selectedDirectory
          ? `${selectedDirectory}/${oldFolderName}`
          : oldFolderName;

        const response = await axios.patch(
          `${BASE_URL}/api/files/rename/`,
          {
            tab_type: getTabType(),
            old_path: oldPath,
            new_name: newFolderName,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          fetchDirectories();
          setEditingFolder(null);
          setEditFolderValue("");

          // If we're currently in the folder that was renamed, update the selected directory
          if (selectedDirectory === oldFolderName) {
            dispatch(selectDirectory(newFolderName));
          }
        }
      } catch (error) {
        console.error(
          "Failed to rename folder:",
          error.response?.data || error.message
        );
        alert("Failed to rename folder. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // If no change or empty name, just cancel
      cancelEditingFolder();
    }
  };

  const handleDeleteFolder = async (folderName) => {
    if (
      window.confirm(`Are you sure you want to delete folder "${folderName}"?`)
    ) {
      setLoading(true);
      try {
        const response = await axios.delete(`${BASE_URL}/api/files/`, {
          params: {
            tab_type: getTabType(),
            file_path: `${selectedDirectory}/${folderName}`,
          },
        });

        if (response.status === 200) {
          fetchDirectories();
          // If we're currently in the folder that was deleted, go back to parent
          if (selectedDirectory === folderName) {
            dispatch(selectDirectory(""));
          }
        }
      } catch (error) {
        console.error(
          "Failed to delete folder:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteFile = async (file) => {
    if (
      window.confirm(`Are you sure you want to delete file "${file.name}"?`)
    ) {
      setLoading(true);
      try {
        const response = await axios.delete(`${BASE_URL}/api/files/`, {
          params: {
            tab_type: getTabType(),
            file_path: `${selectedDirectory}/${file.name}`,
          },
        });

        if (response.status === 200) {
          fetchDirectories();
        }
      } catch (error) {
        console.error(
          "Failed to delete file:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadFile = async (file) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/files/download/`, {
        params: {
          tab_type: getTabType(),
          file_path: `${selectedDirectory}/${file.name}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(
        "Failed to download file:",
        error.response?.data || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(newOrder);
  };

  const sortedFiles = directories
    .filter((item) => item.type === "file")
    .filter((file) =>
      file.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortColumn === "name") {
        return sortOrder === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      // Add other columns sorting logic as needed
      return 0;
    });

  const filteredFolders = directories
    .filter((item) => item.type === "folder")
    .filter((folder) =>
      folder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const formatFileSize = (size) => {
    if (!size) return "N/A";
    const units = ["B", "KB", "MB", "GB"];
    let formattedSize = parseInt(size);
    let unitIndex = 0;

    while (formattedSize >= 1024 && unitIndex < units.length - 1) {
      formattedSize /= 1024;
      unitIndex++;
    }

    return `${formattedSize.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-sm">
          <h3 className="text-lg sm:text-xl font-semibold mb-4">
            {modalType === "upload" ? "Upload File" : "Create New Folder"}
          </h3>

          {modalType === "upload" ? (
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Select File</label>
              <input
                type="file"
                onChange={(e) => setFileToUpload(e.target.files[0])}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-sm text-gray-500 mt-1 truncate">
                {fileToUpload
                  ? `Selected: ${fileToUpload.name}`
                  : "No file selected"}
              </p>
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Folder Name</label>
              <input
                type="text"
                placeholder="Enter folder name"
                value={newDirectoryName}
                onChange={(e) => setNewDirectoryName(e.target.value)}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddDirectory();
                  }
                }}
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-100 text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={
                modalType === "upload" ? handleUploadFile : handleAddDirectory
              }
              disabled={
                modalType === "upload" ? !fileToUpload : !newDirectoryName
              }
              className={`px-4 py-2 text-white rounded-md text-sm sm:text-base ${
                (modalType === "upload" ? !fileToUpload : !newDirectoryName)
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {modalType === "upload" ? "Upload" : "Create"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFileManager = () => (
    <div className="flex flex-col md:flex-row h-[calc(100vh-200px)] bg-gray-100 rounded-lg overflow-hidden">
      {/* Sidebar for Folders */}
      <div className="md:w-1/4 bg-gray-800 text-white p-4 overflow-y-auto">
        <div className="flex items-center space-x-2 mb-6">
          <FiFolder className="text-blue-400" />
          <h2
            className="text-lg font-semibold cursor-pointer hover:text-blue-400 transition-colors"
            onClick={() => dispatch(selectDirectory(""))}
          >
            My Files
          </h2>
        </div>

        {selectedDirectory && (
          <div
            className="flex items-center space-x-2 mb-4 p-2 bg-gray-700 rounded cursor-pointer hover:bg-gray-600"
            onClick={() => dispatch(selectDirectory(""))}
          >
            <FiArrowLeft />
            <span>Back to Root</span>
          </div>
        )}

        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 pl-8 bg-gray-700 border-none rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FiSearch className="absolute left-2 top-3 text-gray-400" />
          </div>
        </div>

        <ul className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto">
          {filteredFolders.map((folder, idx) => (
            <li
              key={idx}
              className="flex justify-between items-center p-2 rounded hover:bg-gray-700 transition-colors"
            >
              {editingFolder === folder.name ? (
                <div className="flex items-center w-full">
                  <FiFolder className="text-blue-400 flex-shrink-0 mr-2" />
                  <div className="flex items-center flex-1 min-w-0">
                    <input
                      type="text"
                      value={editFolderValue}
                      onChange={(e) => setEditFolderValue(e.target.value)}
                      className="flex-1 p-1 text-sm bg-gray-600 border border-gray-500 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleEditFolder(folder.name, editFolderValue);
                        } else if (e.key === "Escape") {
                          cancelEditingFolder();
                        }
                      }}
                      onBlur={() =>
                        handleEditFolder(folder.name, editFolderValue)
                      }
                    />
                    <div className="flex ml-1">
                      <button
                        onClick={() =>
                          handleEditFolder(folder.name, editFolderValue)
                        }
                        className="p-1 text-green-400 hover:text-green-300 transition-colors"
                        title="Save"
                      >
                        <FiCheck size={14} />
                      </button>
                      <button
                        onClick={cancelEditingFolder}
                        className="p-1 text-red-400 hover:text-red-300 transition-colors"
                        title="Cancel"
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="flex items-center space-x-2 cursor-pointer flex-1 min-w-0"
                    onClick={() => dispatch(selectDirectory(folder.name))}
                  >
                    <FiFolder className="text-blue-400 flex-shrink-0" />
                    <span className="truncate">{folder.name}</span>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingFolder(folder.name);
                      }}
                      className="text-gray-400 hover:text-blue-400 transition-colors flex-shrink-0 ml-2 p-1"
                      title="Rename folder"
                    >
                      <FiEdit size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.name);
                      }}
                      className="text-gray-400 hover:text-red-400 transition-colors flex-shrink-0 ml-1 p-1"
                      title="Delete folder"
                    >
                      <FiTrash size={16} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Main Section for Files */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="font-semibold text-lg text-gray-800">
              {selectedDirectory || "Root Directory"}
            </h2>
            <p className="text-sm text-gray-500">{getTabType()} Resources</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => openModal("folder")}
              className="flex items-center space-x-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
              title="New Folder"
            >
              <FiPlus size={16} />
              <span className="sm:inline">Folder</span>
            </button>

            <button
              onClick={() => {
                if (selectedDirectory) openModal("upload");
              }}
              disabled={!selectedDirectory}
              className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm transition-colors ${
                !selectedDirectory
                  ? "bg-green-200 text-green-400 cursor-not-allowed"
                  : "bg-green-50 text-green-600 hover:bg-green-100"
              }`}
              title={
                !selectedDirectory
                  ? "Select a folder to upload files"
                  : "Upload File"
              }
            >
              <FiUpload size={16} />
              <span className="sm:inline">Upload</span>
            </button>

            <button
              onClick={fetchDirectories}
              className="flex items-center space-x-1 px-3 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
              title="Refresh"
            >
              <FiRefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Search and file listing */}
        <div className="flex-1 p-3 sm:p-4 overflow-auto">
          {selectedDirectory && (
            <div className="mb-4 relative">
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
            </div>
          )}
          {sortedFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 sm:h-64 text-gray-500">
              <FiFile size={48} className="mb-4 opacity-30" />
              <p className="text-lg text-center">No files found</p>
              <p className="text-sm text-center">
                Upload files or create a new folder
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th
                      className="py-3 px-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Name</span>
                        {sortColumn === "name" && (
                          <span className="text-xs">
                            {sortOrder === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="py-3 px-4 font-medium text-gray-600 hidden sm:table-cell">
                      Size
                    </th>
                    <th className="py-3 px-4 font-medium text-gray-600 hidden lg:table-cell">
                      Type
                    </th>
                    <th className="py-3 px-4 font-medium text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map((file, idx) => (
                    <tr
                      key={idx}
                      className="border-t hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        {editMode === file.name ? (
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 p-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleEditFile(file.id, editValue);
                                } else if (e.key === "Escape") {
                                  cancelEditing();
                                }
                              }}
                            />
                            <div className="flex">
                              <button
                                onClick={() =>
                                  handleEditFile(file.id, editValue)
                                }
                                className="p-2 bg-green-500 text-white rounded-tr"
                                title="Save"
                              >
                                <FiCheck size={18} />
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="p-2 bg-red-500 text-white rounded-br"
                                title="Cancel"
                              >
                                <FiX size={18} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <FiFile className="text-gray-400 flex-shrink-0" />
                            <span className="cursor-pointer hover:text-blue-600 transition-colors truncate">
                              {file.name}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600 hidden sm:table-cell">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 hidden lg:table-cell">
                        {getFileExtension(file.name)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => handleDownloadFile(file)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                            title="Download"
                          >
                            <FiDownload size={18} />
                          </button>
                          {/* <button
                            onClick={() => startEditingFile(file)}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded-full transition-colors"
                            title="Rename"
                          >
                            <FiEdit size={18} />
                          </button> */}
                          <button
                            onClick={() => handleDeleteFile(file)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                            title="Delete"
                          >
                            <FiTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`flex-1 bg-gray-50 ${
          isSideBarOpen ? "sm:ml-80" : "sm:ml-20"
        } min-h-screen transition-all duration-300`}
      >
        <div className="p-3 sm:p-6 min-h-screen">
          <div className="max-w-8xl mx-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
              Resources
            </h1>

            {/* Main Tabs */}
            <div className="flex justify-center mb-6 sm:mb-8 overflow-x-auto pb-2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab("students")}
                    className={`px-4 sm:px-8 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap ${
                      activeTab === "students"
                        ? "bg-indigo-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Students
                  </button>
                  <button
                    onClick={() => setActiveTab("team")}
                    className={`px-4 sm:px-8 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap ${
                      activeTab === "team"
                        ? "bg-indigo-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Internal Team
                  </button>
                </div>
              </div>
            </div>

            {/* Students Section */}
            {activeTab === "students" && (
              <>
                <div className="flex justify-center mb-4 sm:mb-6 overflow-x-auto pb-2">
                  <div className="bg-white rounded-full shadow-md p-1 inline-flex">
                    <button
                      onClick={() => setActiveTab2("fileManager")}
                      className={`px-3 sm:px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap ${
                        activeTab2 === "fileManager"
                          ? "bg-blue-500 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      File Manager
                    </button>
                    <button
                      onClick={() => setActiveTab2("pageInfo")}
                      className={`px-3 sm:px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap ${
                        activeTab2 === "pageInfo"
                          ? "bg-blue-500 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Page wise Info
                    </button>
                  </div>
                </div>

                {activeTab2 === "fileManager" && renderFileManager()}

                {activeTab2 === "pageInfo" && (
                  <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
                    <DocumentUpload />
                  </div>
                )}
              </>
            )}

            {/* Internal Team Section */}
            {activeTab === "team" && renderFileManager()}
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl flex flex-col items-center m-4">
            <div className="w-12 sm:w-16 h-12 sm:h-16 border-4 border-t-indigo-600 border-blue-400 rounded-full animate-spin mb-4"></div>
            <div className="text-base sm:text-lg font-semibold text-gray-800">
              Processing...
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-2">
              Please wait while your request is being processed
            </div>
          </div>
        </div>
      )}

      {renderModal()}
    </>
  );
};

export default Resources;
